import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { FilmSlate, Camera, Sparkle, PaintBrush } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const enhancementStyles = [
  {
    id: 'cinematic',
    label: 'Cinematic',
    icon: FilmSlate,
    description: 'Film-like dramatic lighting, shallow depth of field, movie quality',
    color: '#FF5D01'
  },
  {
    id: 'realistic',
    label: 'Realistic',
    icon: Camera,
    description: 'Natural lighting, documentary style, authentic atmosphere',
    color: '#10B981'
  },
  {
    id: 'dramatic',
    label: 'Dramatic',
    icon: Sparkle,
    description: 'High contrast, intense shadows, powerful composition',
    color: '#8B5CF6'
  },
  {
    id: 'artistic',
    label: 'Artistic',
    icon: PaintBrush,
    description: 'Painterly quality, expressive, fine art aesthetic',
    color: '#F59E0B'
  }
];

export const EnhancePromptButtons = ({ sceneId, currentPrompt, onEnhanced, compact = false }) => {
  const [enhancing, setEnhancing] = useState(null);

  const handleEnhance = async (style) => {
    if (!sceneId || !currentPrompt) {
      toast.error('No scene or prompt to enhance');
      return;
    }

    setEnhancing(style);
    try {
      const res = await axios.put(`${API}/scenes/${sceneId}/enhance?style=${style}`);
      toast.success(`Applied ${style} enhancement!`);
      if (onEnhanced) {
        onEnhanced(res.data.scene);
      }
    } catch (err) {
      toast.error('Failed to enhance prompt');
    } finally {
      setEnhancing(null);
    }
  };

  if (compact) {
    return (
      <TooltipProvider>
        <div className="flex items-center gap-1">
          {enhancementStyles.map((style) => {
            const Icon = style.icon;
            const isEnhancing = enhancing === style.id;
            
            return (
              <Tooltip key={style.id}>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEnhance(style.id)}
                    disabled={!!enhancing}
                    className="h-7 w-7 p-0 hover:bg-[#262626]"
                    data-testid={`enhance-${style.id}-btn`}
                  >
                    {isEnhancing ? (
                      <div className="w-3 h-3 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: style.color }} />
                    ) : (
                      <Icon size={14} weight="fill" style={{ color: style.color }} />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="bg-[#171717] border-[#333333] text-white">
                  <p className="font-medium">{style.label}</p>
                  <p className="text-xs text-[#A1A1AA]">{style.description}</p>
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      </TooltipProvider>
    );
  }

  return (
    <div className="space-y-2">
      <div className="text-xs text-[#A1A1AA] font-medium">Enhance Style</div>
      <div className="grid grid-cols-2 gap-2">
        {enhancementStyles.map((style) => {
          const Icon = style.icon;
          const isEnhancing = enhancing === style.id;
          
          return (
            <Button
              key={style.id}
              variant="outline"
              size="sm"
              onClick={() => handleEnhance(style.id)}
              disabled={!!enhancing}
              className="border-[#333333] text-white hover:bg-[#262626] justify-start gap-2 h-auto py-2"
              data-testid={`enhance-${style.id}-btn`}
            >
              {isEnhancing ? (
                <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: style.color }} />
              ) : (
                <Icon size={16} weight="fill" style={{ color: style.color }} />
              )}
              <div className="text-left">
                <div className="text-xs font-medium">{style.label}</div>
              </div>
            </Button>
          );
        })}
      </div>
    </div>
  );
};
